<template>
  <div class="two-step-authentication-page overflow-x-hide">
    <div class="container no-padding">
      <div class="row no-margin">
        <div class="col-md-12" :style="{ 'min-height': dyn_height }">
          <HeaderLogo :page-type="'404'" :light_logo="true" />

          <div
            class="row no-padding main-body h-100"
            :style="{ height: windowHeight }"
          >
            <div class="col-md-12 flex-container">
              <BrandPattern page="TSA" />
            </div>
          </div>
        </div>

        <errorhandler
          v-if="showProspectCallbackError || null"
          :prospect-callback-error-msg="prospectCallbackErrorMsg"
          @reset-show-prospect-callback-error="resetShowProspectCallbackError"
        />

        <!-- MFA Verify Modal -->
        <div
          id="twoStepAuthenticationModal"
          ref="twoStepAuthenticationModal"
          class="modal fade two-step-authentication-modal tw-block"
          role="dialog"
          aria-labelledby="twoStepAuthenticationModalLabel"
          data-backdrop="false"
        >
          <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
              <div class="modal-header">
                <button
                  type="button"
                  class="close"
                  aria-label="Close"
                  @click="onClickClose"
                >
                  <img src="@/assets/img/close.svg" />
                </button>
              </div>
              <div class="modal-body account-modal">
                <div class="modal-body-sec1">
                  {{ getUiText("user-mfa-login-heading-1") }}
                </div>
                <div class="auth-modal-text">
                  {{ getUiText("user-mfa-login-heading-sub-1") }}
                </div>
                <div class="form-group">
                  <label for="id" class="control-label input-label-form">{{
                    getUiText("user-mfa-login-enter-code-heading-1")
                  }}</label>
                  <input
                    id="code"
                    ref="code"
                    v-model="verifyToken"
                    data-cy="two-step-authentication-code"
                    type="text"
                    :class="`form-control form-control-lg password-input ${
                      !totpValid ? 'invalid-input' : ''
                    }`"
                    tabindex="0"
                    @input="onVerifyTokenChange"
                  />
                  <div
                    v-if="!totpValid"
                    class="error-msg"
                    :class="!totpValid ? 'mg-top-19' : ''"
                  >
                    <span>{{ getUiText("user-mfa-setup-error-valid-1") }}</span>
                  </div>
                </div>
                <div class="letsgo-btn">
                  <button
                    id="signin-button"
                    data-cy="two-step-authentication-submit"
                    type="button"
                    class="btn btn-primary letsgo-button"
                    :disabled="disableButton ? true : false"
                    @click="submit"
                  >
                    {{
                      getUiText(
                        "app-settings-domain-verify-modal-verify-button-1",
                      )
                    }}
                  </button>
                  <span tabindex="0" class="forgot-text-button tw-mr-2">{{
                    getUiText("user-mfa-login-lost-mfa-authenticator-code-1")
                  }}</span>
                  <ButtonShowDuplicateAssessment
                    :id-popover="'popover-two-step-authentication'"
                    :custom-class="'tw-w-[max-content]  md:tw-max-w-[350px] lg:tw-max-w-[470px]'"
                    :classes="`!tw-leading-none `"
                  >
                    <div class="tw-p-[15px] tw-flex tw-flex-col tw-gap-3">
                      <div
                        class="tw-text-[14px] tw-font-semibold tw-text-[#211f58] tw-font-poppins"
                      >
                        {{
                          getUiText(
                            "user-mfa-login-lost-mfa-authenticator-code-tooltip-title-1",
                          )
                        }}
                      </div>
                      <CustomDescriptionContent
                        :description="
                          getUiText(
                            'user-mfa-login-lost-mfa-authenticator-code-tooltip-paragraph-1',
                          )
                        "
                        :class="`!tw-p-0 tw-text-[13px] tw-text-[#878787]`"
                      >
                      </CustomDescriptionContent>
                      <CustomDescriptionContent
                        :description="
                          getUiText(
                            'user-mfa-login-lost-mfa-authenticator-code-tooltip-paragraph-2',
                          )
                        "
                        :class="`!tw-p-0 tw-text-[13px] tw-text-[#878787]`"
                      >
                      </CustomDescriptionContent>
                    </div>
                  </ButtonShowDuplicateAssessment>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import HeaderLogo from "@/components/common/headerLogoWithoutLangPicker.vue";
import BrandPattern from "@/components/prospect/brandPattern/brandPattern2.vue";
import $ from "jquery";
import userService from "@/services/user/userService";
import errorhandler from "@/components/common/exceptionHandler.vue";
import Utils from "@/utils/utilities";
import { load } from "recaptcha-v3";
import store from "@/store";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  ref,
  watch,
  onBeforeUnmount,
} from "vue";
import router from "@/router";
import { localStorageKey } from "@/utils/constants";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import { showErrorAPI } from "@/services/apiRoutes";
import ButtonShowDuplicateAssessment from "@/components/common/setting/buttonShowDuplicateAssessment.vue";
import CustomDescriptionContent from "../common/customDescriptionContent.vue";
import { submitMFAVerification } from "@/services/user/twoStepAuthenticationService";

const dyn_height = ref("0");
const disableButton = ref(true);
const windowHeight = ref("");
const showProspectCallbackError = ref(false);
const prospectCallbackErrorMsg = ref("");
const verifyToken = ref("");
const twoStepAuthenticationModal = ref<null>(null);
const totpValid = ref(true);

const languageSelected = computed(
  () => store.state.LanguagePicker.languageSelected,
);
const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);

const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);

// get heightSetter() {
//   return { "min-height": `${this.windowHeight}` };
// }

const onVerifyTokenChange = (e) => {
  if (e.target.value && e.target.value.length >= 1) {
    // riggered by a user entering a non-numeric character into the field
    if (isNaN(e.target.value)) {
      totpValid.value = false;
      disableButton.value = true;
    } else {
      if (e.target.value.length >= 6) {
        disableButton.value = false;
      } else {
        disableButton.value = true;
      }
      totpValid.value = true;
    }
  } else {
    totpValid.value = true;
    disableButton.value = true;
  }
};

const code: any = ref();
const onResize = () => {
  dyn_height.value = (window.innerHeight + "px") as string;
  windowHeight.value = "" + window.innerHeight + "px !important ";
  code.value && code.value.focus();
};

onMounted(() => {
  nextTick(function () {
    window.addEventListener("resize", onResize);
    onResize();
  });

  // show twoStepAuthentication Modal
  document.getElementById("twoStepAuthenticationModal")?.classList.add("show");

  const body = $("body");
  if (!body.hasClass("page-opacity")) {
    body.addClass("page-opacity page-bg");
  }

  setLanguages();
  listeningEvent();
});

onBeforeUnmount(() => {
  EventEmitter.off(EmitterEvents.EVENT_KEYBOARD_ENTER);
  EventEmitter.off(EmitterEvents.EVENT_KEYBOARD_ESC);
});

const setLanguages = async () => {
  if (
    localStorage.getItem(localStorageKey.languagesData) == null ||
    localStorage.getItem(localStorageKey.languageList) == null
  ) {
    await Utils.getV3Recaptcha().then(
      async (finalKey: any) => {
        const captchKey = finalKey;
        const recaptcha = await load(`${captchKey}`, {
          autoHideBadge: true,
        });
        recaptcha.execute("submit").then((token) => {
          localStorage.setItem(localStorageKey.recaptcha, token);
          store.dispatch("LanguagePicker/setLanguages", token);
        });
        recaptcha.execute("submit").then((token) => {
          localStorage.setItem(localStorageKey.recaptcha, token);
          store.dispatch("LanguagePicker/setLanguageList", token);
        });
      },
      (error: any) => {
        if (error && error.data && error.data.error && error.data.error.type) {
          if (error.data.error.type === "exc_session_invalid-1") {
            sessionStorage.setItem(
              "prospectCallbackErrorInfo",
              JSON.stringify(error.data.error),
            );
            router.push({ path: "/" });
          } else {
            showErrorInfo(error.data.error);
          }
        } else if (error.data.message === "invalid csrf token") {
          sessionStorage.setItem(
            "prospectCallbackErrorInfo",
            JSON.stringify(error.data),
          );
          router.push({ path: "/" });
        } else {
          showErrorInfo(error.data);
        }
      },
    );
  }
};

const submit = () => {
  submitMFAVerification(
    verifyToken.value,
    disableButton.value,
    totpValid.value,
    {
      getUiText,
      showToastError,
      routerPush: (path) => router.push(path),
      routerGoBack: () => router.go(-1),
      sessionStorageSetItem: (key, value) => sessionStorage.setItem(key, value),
      afterLoginActions: Utils.afterLoginActions,
    },
  );
};

const listeningEvent = () => {
  EventEmitter.on(EmitterEvents.EVENT_KEYBOARD_ENTER, submit);
  EventEmitter.on(EmitterEvents.EVENT_KEYBOARD_ESC, onClickClose);
};

const showErrorInfo = (errorInfo: any) => {
  if (errorInfo && errorInfo !== "") {
    errorInfo =
      typeof errorInfo === "string" ? JSON.parse(errorInfo) : errorInfo;
    if (errorInfo && Object.keys(errorInfo).length > 0 && errorInfo.message) {
      showProspectCallbackError.value = true;
      prospectCallbackErrorMsg.value = errorInfo.message;
    }
  }
};

const showToastError = (message) => {
  const emitData: IToastMessage = {
    variant: ToastVariant.ERROR,
    message,
  };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const resetShowProspectCallbackError = () => {
  showProspectCallbackError.value = false;
  prospectCallbackErrorMsg.value = "";
};

const onClickClose = () => {
  router.go(-1);
};

onBeforeMount(() => {
  removeListeningEvent();
  const body = $("body");
  if (!body.hasClass("page-opacity")) {
    body.addClass("page-opacity page-bg page-shadow");
  }
});

const removeListeningEvent = () => {
  EventEmitter.off(EmitterEvents.EVENT_KEYBOARD_ENTER);
  EventEmitter.off(EmitterEvents.EVENT_KEYBOARD_ESC);
};

const getUiText = (id: string, isArray = false) => {
  return Utils.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};
</script>

<style lang="scss" scoped>
.two-step-authentication-page {
  background: #0345bd;

  .form-control:focus {
    box-shadow: none;
  }

  .form-control::placeholder {
    color: #b7b7b7;
    font-size: 22px;
  }

  .two-step-authentication-modal {
    height: 100%;
    width: 100%;
    pointer-events: none;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);

    .modal-dialog {
      //transition: none;
      min-width: 630px;
    }

    .modal-header {
      border: none;
      padding: 10px 10px 0px 10px;
      justify-content: flex-end;
      .close {
        float: right;
        opacity: 1;
        margin-right: 10px;
        margin-top: 10px;
        position: relative;
        padding: 0;
        z-index: 999;

        img {
          width: 19px;
          height: 19px;
        }
      }
    }

    .modal-content {
      box-shadow: 2px 0px 20px rgba(33, 31, 88, 0.1);
      border-radius: 6px;
      border: none;
    }

    .modal-body {
      padding: 35px 70px 48px 70px;

      .flex-container {
        position: absolute;
        bottom: 0px;
        height: 100%;
      }

      .modal-body-sec1 {
        color: #0345bd;
        font-size: 26px;
        font-weight: 600;
        line-height: 39px;
      }

      .auth-modal-text {
        font-size: 15px;
        color: #231f20;
        line-height: 22px;
        font-weight: 400;
        margin-top: 15px;
      }

      .letsgo-btn {
        margin-top: 38px;
        display: flex;
        align-items: center;
      }

      .input-label-form {
        font-weight: 500;
        font-size: 12px;
        color: #231f20;
        line-height: 18px;
      }

      .password-input {
        border: none;
        font-style: normal;
        font-weight: 500;
        font-size: 22px;
        line-height: 33px;
        color: #0345bd;
        border-left: 1px solid rgba(183, 183, 183, 0.3);
        border-radius: unset;
        letter-spacing: 0.25em;
      }

      .password-input {
        font-size: 22px;
      }

      .form-group {
        margin-top: 15px;

        label {
          margin-bottom: 14px;
          margin-top: 14px;
        }
      }

      .password-left-conditions {
        margin-bottom: 0px;
      }

      .letsgo-button {
        width: 135px;
        height: 45px;
        left: 72px;
        border-radius: 29.4638px;
        background: #0345bd;
        font-style: normal;
        font-weight: 600;
        font-size: 18px;
        line-height: 27px;
        box-shadow: 0px 2.18987px 4.37975px rgba(33, 31, 88, 0.25);
        text-transform: uppercase;
      }

      .letsgo-btn button:disabled {
        color: #ffffff;
        background: #eeeff0;
        border-color: #eeeff0;
      }

      .forgot-text-button {
        font-size: 12px;
        line-height: 27px;
        padding-left: 30px;
        color: #231f20;
      }
    }
  }
}

@media (max-width: 767px) {
  .two-step-authentication-page {
    .two-step-authentication-modal {
      .modal-header {
        .close {
          margin-top: 3px !important;

          img {
            width: 17px;
            height: 17px;
          }
        }
      }

      .modal-dialog {
        min-width: unset;
      }

      .modal-body {
        padding: 18px 30px 30px 30px;

        .modal-body-sec1 {
          font-size: 22px;
        }

        .form-group {
          margin-top: 24px !important;
        }

        .letsgo-button {
          width: 136px;
          font-size: 16px;
          line-height: 24px;
        }

        .password-input {
          font-size: 18px;
          padding-right: 0px;
        }

        .password-input::placeholder {
          font-size: 18px;
        }

        .input-label-form {
          font-size: 12px;
          line-height: 18px;
        }

        .forgot-text-button {
          font-size: 12px;
          line-height: 18px;
        }

        .letsgo-btn {
          margin-top: 20px;
        }

        .auth-modal-text {
          font-size: 15px !important;
          line-height: 22px !important;
          margin-top: 8px !important;
        }
      }
    }
  }
}

@media (max-height: 500px) {
  .modal {
    padding-top: 50px;
  }
}

@media (min-width: 3840px) {
  .two-step-authentication-page {
    .two-step-authentication-modal {
      .form-control:focus {
        padding-left: 30px;
        font-size: 38px;
        line-height: 30px;
      }

      .modal-dialog {
        max-width: 1090px !important;
      }

      .modal-body {
        padding: 85px 90px 90px 90px;
      }

      .modal-content {
        border-radius: 15px !important;
      }

      .modal-body-sec1 {
        font-size: 52px !important;
        line-height: 78px !important;
      }

      .form-group {
        margin-top: 35px !important;
      }

      .auth-modal-text {
        font-size: 30px !important;
        line-height: 44px !important;
      }

      .input-label-form {
        font-size: 24px !important;
        line-height: 36px !important;
        margin-bottom: 28px !important;
      }

      .forgot-text-button {
        font-size: 24px !important;
        line-height: 36px !important;
        margin-left: 40px !important;
      }

      .close {
        margin-right: 35px !important;
        margin-top: 40px !important;

        img {
          height: 30px !important;
          width: 30px !important;
        }
      }

      .letsgo-btn {
        margin-top: 76px !important;
      }

      .password-input {
        font-size: 44px !important;
        padding-left: 30px;
        border-left: 2px solid rgba(183, 183, 183, 0.3) !important;
      }

      .password-input {
        font-size: 44px !important;
      }

      .letsgo-button {
        width: 200px !important;
        height: 90px !important;
        font-size: 30px !important;
        border-radius: 41px !important;
      }
    }
  }
}
</style>
